import { useProjectContext } from "@contexts/projects";
import { formatNumberToLocale } from "@utils/formatNumber";
import { STOCK_SOURCE_COPY, STOCK_STALE_DAYS } from "@outlets/configs/trendCopy";

// Resumo do estoque da obra. Lê activeProject.stock (montado no getDetail).
//
// Existe porque estoque lançado e estoque USADO no cálculo são coisas diferentes, e até
// agora a diferença era invisível: com a fonte de estoque em "Não considerar" — o padrão de
// toda empresa — o valor fica gravado e não entra em conta nenhuma. O card mostra os dois
// números lado a lado, mais o que está sendo descartado e a idade do lançamento mais antigo.
//
// Só aparece quando há algo a dizer: obra sem estoque nenhum não ganha card.

const DIVIDER = "1px solid rgba(0, 0, 0, 0.08)";
const WARN_COLOR = "#C0392B";

const daysSince = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.floor((Date.now() - date.getTime()) / 86400000);
};

const plural = (n, singular, pluralForm) => (n === 1 ? singular : pluralForm);

const Row = ({ label, value, muted }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      gap: 16,
      padding: "6px 0",
      opacity: muted ? 0.6 : 1,
    }}
  >
    <span>{label}</span>
    <strong>{formatNumberToLocale(value)}</strong>
  </div>
);

const Warning = ({ children }) => (
  <p style={{ color: WARN_COLOR, margin: "8px 0 0", fontSize: "0.85em" }}>{children}</p>
);

const StockSummaryCard = () => {
  const { activeProject } = useProjectContext();
  const stock = activeProject?.stock;

  // Sem o campo (back-end antigo) ou sem estoque algum: nada a mostrar.
  if (!stock) return null;

  const registered =
    (stock.available_manual || 0) +
    (stock.available_api || 0) +
    (stock.discarded_manual_value || 0) +
    (stock.discarded_api_value || 0);
  if (registered <= 0) return null;

  const sourceLabel = STOCK_SOURCE_COPY[stock.source]?.label ?? stock.source;
  const ignoring = stock.source === "none";
  const age = stock.oldest_reference_date ? daysSince(stock.oldest_reference_date) : null;
  const discardedNodes = (stock.discarded_manual_nodes || 0) + (stock.discarded_api_nodes || 0);
  const discardedValue = (stock.discarded_manual_value || 0) + (stock.discarded_api_value || 0);

  return (
    <div className="card" style={{ padding: 16 }}>
      <h4 style={{ margin: "0 0 4px" }}>Estoque desta obra</h4>
      <p style={{ margin: "0 0 12px", fontSize: "0.85em", opacity: 0.7 }}>
        Fonte configurada: <strong>{sourceLabel}</strong>
      </p>

      <div style={{ borderBottom: DIVIDER, paddingBottom: 8, marginBottom: 8 }}>
        <Row label="Descontado do custo no cálculo" value={stock.applied || 0} />
      </div>

      <Row label="Lançado na plataforma" value={stock.available_manual || 0} muted={ignoring} />
      <Row label="Almoxarifado do Sienge" value={stock.available_api || 0} muted={ignoring} />

      {ignoring && (
        <Warning>
          A fonte de estoque desta empresa está em &ldquo;{sourceLabel}&rdquo;, então nada disso
          está sendo descontado do custo. Você muda isso em Configurações › Tendência e Estoque.
        </Warning>
      )}

      {/* As duas fontes NUNCA são somadas no mesmo item: quando o Sienge cobre o item, ele
          vence e o lançamento manual daquele item é ignorado. Sem este aviso, escolher
          "almoxarifado complementado pelo manual" apagaria valor em silêncio. */}
      {stock.shadowed_manual_value > 0 && (
        <Warning>
          {formatNumberToLocale(stock.shadowed_manual_value)} do estoque lançado na plataforma
          está em {stock.shadowed_manual_nodes}{" "}
          {plural(stock.shadowed_manual_nodes, "item que o Sienge também cobre", "itens que o Sienge também cobre")}
          . Se a fonte for &ldquo;almoxarifado complementado pelo manual&rdquo;, nesses itens vale
          o Sienge e esse valor não é usado.
        </Warning>
      )}

      {discardedNodes > 0 && (
        <Warning>
          {discardedNodes}{" "}
          {plural(discardedNodes, "item de estoque não corresponde", "itens de estoque não correspondem")}{" "}
          a nenhum item do orçamento desta obra ({formatNumberToLocale(discardedValue)}) e{" "}
          {plural(discardedNodes, "está", "estão")} fora do cálculo.
        </Warning>
      )}

      {age !== null && age > STOCK_STALE_DAYS && (
        <Warning>
          O estoque mais antigo considerado é de{" "}
          {new Date(stock.oldest_reference_date).toLocaleDateString("pt-BR")} — há {age} dias.
          Estoque antigo continua sendo descontado do custo; confira se ainda é válido.
        </Warning>
      )}
    </div>
  );
};

export default StockSummaryCard;
